import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView, useReducedMotion, animate } from 'framer-motion';

// Componentes
import Header from '../components/Header/Header';
import Hero from '../components/Hero/Hero';
import Section from '../components/Section/Section';
import Carousel from '../components/Carousel/Carousel';
import FloatingButtons from '../components/FloatingButtons/FloatingButtons';
import StructuredData from '../components/SEO/StructuredData';
import HowToBookSection from '../components/Home/HowToBookSection';
import CommunitySection from '../components/Home/CommunitySection';
import ProfessionalsSection from '../components/Home/ProfessionalsSection';
import PricesSection from '../components/Home/PricesSection';
import DisordersSection from '../components/Home/DisordersSection';
import WhyUsSection from '../components/Home/WhyUsSection';
import LocationSection from '../components/Home/LocationSection';

// Datos
import { carouselImages } from '../data/carousel';

// Hooks
import { useScrollEffects } from '../hooks/useScrollEffects';
import { useCarouselValues } from '../hooks/useCarouselValues';

// Schemas SEO
import { medicalBusinessSchema, websiteSchema, faqSchema, medicalWebPageSchema } from '../data/schemas';

interface AnimatedStatNumberProps {
  value: number;
  prefix?: string;
  duration?: number;
}

/**
 * Contador que asciende de 0 al valor final cuando entra en viewport (una sola vez).
 * Respeta prefers-reduced-motion: sin animar, muestra el valor final directo.
 */
const AnimatedStatNumber: React.FC<AnimatedStatNumberProps> = ({ value, prefix = '', duration = 1.4 }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.6 });
  const shouldReduceMotion = useReducedMotion();
  const [displayValue, setDisplayValue] = useState(shouldReduceMotion ? value : 0);

  useEffect(() => {
    if (!isInView) return;

    if (shouldReduceMotion) {
      setDisplayValue(value);
      return;
    }

    const controls = animate(0, value, {
      duration,
      ease: 'easeOut',
      onUpdate: (latest) => setDisplayValue(Math.round(latest))
    });

    return () => controls.stop();
  }, [isInView, shouldReduceMotion, value, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {displayValue}
    </span>
  );
};

/**
 * "24/7" no es un valor numérico que pueda ascender de forma natural,
 * así que en vez de count-up usa fade + scale in al entrar en viewport.
 */
const AnimatedAvailability: React.FC = () => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.span
      initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.85 }}
      whileInView={shouldReduceMotion ? undefined : { opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      24/7
    </motion.span>
  );
};

/**
 * Página principal de la aplicación
 * Conectemos Chile - Clínica de Atención Psicológica
 */
const HomePage: React.FC = () => {
  const [showPreloader, setShowPreloader] = useState(true);
  const { scrollToElement } = useScrollEffects();
  const navigate = useNavigate();
  
  // Hook para sincronizar valores con carrusel
  useCarouselValues();

  // Ocultar preloader después de cargar
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPreloader(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* Meta información y SEO */}
      <Helmet>
        <title>Conectemos Chile | Clínica de Atención Psicológica en Santiago</title>
        <meta 
          name="description" 
          content="Clínica de atención psicológica en Chile especializada en diversos trastornos como ansiedad, depresión y estrés. Agenda tu hora con nuestros profesionales y comienza tu camino hacia el bienestar emocional." 
        />
        <meta 
          name="keywords" 
          content="psicología chile, terapia santiago, salud mental, psicólogos profesionales, trastornos psicológicos, ansiedad, depresión, estrés, bienestar emocional, terapia online" 
        />
        
        {/* Meta tags adicionales para SEO */}
        <meta name="author" content="Conectemos Chile" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="revisit-after" content="7 days" />
        <meta name="language" content="es-CL" />
        <meta name="geo.region" content="CL-RM" />
        <meta name="geo.placename" content="Santiago, Chile" />
        <meta name="geo.position" content="-33.4413404;-70.6535814" />
        <meta name="ICBM" content="-33.4413404, -70.6535814" />
        
        {/* Open Graph tags para redes sociales */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Conectemos Chile | Clínica de Atención Psicológica" />
        <meta property="og:description" content="Especialistas en salud mental en Santiago. Terapia profesional para ansiedad, depresión y otros trastornos. Agenda tu consulta hoy." />
        <meta property="og:image" content="https://www.conectemoschile.cl/og/home.jpg?v=1" />
        <meta property="og:url" content="https://www.conectemoschile.cl" />
        <meta property="og:site_name" content="Conectemos Chile" />
        <meta property="og:locale" content="es_CL" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Conectemos Chile | Clínica de Atención Psicológica" />
        <meta name="twitter:description" content="Especialistas en salud mental en Santiago. Agenda tu consulta de terapia psicológica." />
        <meta name="twitter:image" content="https://www.conectemoschile.cl/og/home.jpg?v=1" />
        
        {/* Enlaces canónicos y alternos */}
        <link rel="canonical" href="https://www.conectemoschile.cl" />
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/assets/img/logo.webp" />
        
        {/* Manifest para PWA */}
        <link rel="manifest" href="/manifest.json" />
        <meta name="theme-color" content="#1A3A6B" />
        
        {/* Preconexiones para mejorar rendimiento */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link rel="preconnect" href="https://cdn.jsdelivr.net" />
        <link rel="preconnect" href="https://cdnjs.cloudflare.com" />
        
        {/* Google Fonts */}
        <link
          href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        
        {/* Font Awesome */}
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
        />

      </Helmet>

      {/* Datos estructurados de la página principal */}
      <StructuredData data={medicalBusinessSchema} />
      <StructuredData data={websiteSchema} />
      <StructuredData data={medicalWebPageSchema} />
      <StructuredData data={faqSchema} />

      {/* Preloader */}
      {showPreloader && (
        <div className="preloader" id="preloader">
          <div className="preloader-content">
            <img 
              src="/assets/img/logo.png" 
              alt="Logo Conectemos Chile" 
              className="preloader-logo" 
              width="80" 
              height="80" 
            />
          </div>
        </div>
      )}

      {/* Header de navegación */}
      <Header />

      {/* Contenido principal */}
      <main id="main-content">
        {/* Hero section */}
        <Hero />

        {/* Sección: Por Qué Elegirnos */}
        <WhyUsSection />

        {/* Sección: Precios */}
        <PricesSection />

        {/* Sección: Testimonios */}
        <Section
          id="testimonials"
          title="Lo Que Dicen Nuestros Pacientes"
          subtitle="Experiencias reales de quienes confiaron en nosotros"
        >
          <div className="google-reviews-widget animate-in" style={{ '--delay': '0.2s' } as React.CSSProperties}>
            <div className="elfsight-app-3957d2f5-d307-4f05-8f3c-6bf80c056bd0" data-elfsight-app-lazy></div>
          </div>
        </Section>

        {/* Sección: ¿Cómo agendar? */}
        <HowToBookSection />

        {/* Sección: Nuestros Profesionales */}
        <ProfessionalsSection />

        {/* Sección: Quiénes Somos */}
        <Section
          id="about"
          title="Quiénes Somos"
          subtitle="Un centro de excelencia en salud mental de pareja y bienestar emocional"
          className="about"
        >
          <div className="about-content-modern">
            <div className="about-visual-section">
              <div className="about-carousel-container animate-in" style={{ '--delay': '0.2s' } as React.CSSProperties}>
                <div className="carousel-wrapper-with-badge">
                  <Carousel
                    images={carouselImages}
                    autoplay={true}
                    interval={5000}
                    className="space-carousel"
                  />
                  <div className="badge-inside-carousel">
                    <i className="fas fa-heart-circle-check"></i>
                    <span>+500 Pacientes Atendidos</span>
                  </div>
                  
                  {/* Valores integrados como overlay del carrusel */}
                  <div className="carousel-values-overlay">
                    <div className="values-slide" data-slide="0">
                      <div className="value-pair">
                        <div className="value-text">Empatía</div>
                        <div className="value-text">Confianza</div>
                      </div>
                    </div>
                    <div className="values-slide" data-slide="1">
                      <div className="value-pair">
                        <div className="value-text">Excelencia</div>
                        <div className="value-text">Respeto</div>
                      </div>
                    </div>
                    <div className="values-slide" data-slide="2">
                      <div className="value-pair">
                        <div className="value-text">Compromiso</div>
                        <div className="value-text">Innovación</div>
                      </div>
                    </div>
                    <div className="values-slide" data-slide="3">
                      <div className="value-pair">
                        <div className="value-text">Calidez Humana</div>
                        <div className="value-text">Integridad</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="about-stats">
                <div className="stat-item">
                  <div className="stat-number">
                    <AnimatedStatNumber value={8} prefix="+" />
                  </div>
                  <div className="stat-label">Años de experiencia clínica</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    <AnimatedAvailability />
                  </div>
                  <div className="stat-label">Disponibilidad</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    <AnimatedStatNumber value={2} prefix="+" />
                  </div>
                  <div className="stat-label">Profesionales</div>
                </div>
              </div>
            </div>

            <div className="about-content-section">
              <div className="about-intro animate-in" style={{ '--delay': '0.3s' } as React.CSSProperties}>
                <h3 className="intro-title-original">
                  Especialistas en terapia de parejas y bienestar emocional
                </h3>
                <p className="intro-text">
                  En <strong>Conectemos Chile</strong> creemos en un espacio sin juicios, donde evidencia científica y calidez humana te acompañan.
                </p>
              </div>

            </div>
          </div>
        </Section>

        {/* Sección: Trastornos */}
        <DisordersSection />

        {/* Sección: Comunidad y Talleres */}
        <CommunitySection />

        {/* Sección: Contáctanos / Cómo llegar (id="contact") */}
        <LocationSection />
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path
              fill="#1A3A6B"
              fillOpacity="0.05"
              d="M0,192L48,208C96,224,192,256,288,261.3C384,267,480,245,576,218.7C672,192,768,160,864,165.3C960,171,1056,213,1152,224C1248,235,1344,213,1392,202.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>

        <div className="container">
          <div className="footer-content">
            {/* Información principal */}
            <div className="footer-about">
              <div className="footer-logo">
                <img
                  src="/assets/img/logo.png"
                  alt="Logo Conectemos Chile"
                  width="40"
                  height="40"
                  loading="lazy"
                  style={{ width: '40px', height: '40px' }}
                />
                <h3>Conectemos Chile</h3>
              </div>
              <p>
                Centro de Atención Psicológica especializado en terapia de parejas e individual. Comprometidos con tu bienestar emocional en Santiago.
              </p>
              {/* Visión y misión: salieron de "Quiénes Somos" (2026-10-21) para
                  que la home quede enfocada en el embudo de cierre. */}
              <dl className="footer-purpose">
                <div>
                  <dt>Visión</dt>
                  <dd>Ser referentes en salud mental de pareja en Santiago, con terapia accesible y transformadora.</dd>
                </div>
                <div>
                  <dt>Misión</dt>
                  <dd>Ofrecer terapia de parejas con base en evidencia científica, para fortalecer vínculos y bienestar emocional.</dd>
                </div>
              </dl>
            </div>

            {/* Enlaces rápidos */}
            <div className="footer-links-container">
              <h4 className="footer-heading">Enlaces Rápidos</h4>
              <ul className="footer-links">
                <li>
                  <a href="#home" onClick={(e) => { e.preventDefault(); scrollToElement('#home'); }}>
                    Inicio
                  </a>
                </li>
                <li>
                  <a href="#about" onClick={(e) => { e.preventDefault(); scrollToElement('#about'); }}>
                    Quiénes Somos
                  </a>
                </li>
                <li>
                  <a href="#professionals" onClick={(e) => { e.preventDefault(); scrollToElement('#professionals'); }}>
                    Nuestros Profesionales
                  </a>
                </li>
                <li>
                  <a href="#why-us" onClick={(e) => { e.preventDefault(); scrollToElement('#why-us'); }}>
                    Por Qué Elegirnos
                  </a>
                </li>
                <li>
                  <a href="#disorders" onClick={(e) => { e.preventDefault(); scrollToElement('#disorders'); }}>
                    Trastornos que Tratamos
                  </a>
                </li>
                <li>
                  <a href="#prices" onClick={(e) => { e.preventDefault(); scrollToElement('#prices'); }}>
                    Precios
                  </a>
                </li>
                <li>
                  <a href="#testimonials" onClick={(e) => { e.preventDefault(); scrollToElement('#testimonials'); }}>
                    Testimonios
                  </a>
                </li>
                <li>
                  <a href="/agendar" onClick={(e) => { e.preventDefault(); navigate('/agendar'); }}>
                    Agendar
                  </a>
                </li>
                <li>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToElement('#contact'); }}>
                    Contacto
                  </a>
                </li>
              </ul>
            </div>

            {/* Información de contacto */}
            <div className="footer-contact">
              <h4 className="footer-heading">Contáctanos</h4>
              <p>
                <i className="fas fa-map-marker-alt"></i> Bombero Adolfo Ossa 1010, Oficina 204, Santiago
              </p>
              <p>
                <i className="fas fa-phone"></i> +56 9 7800 9119
              </p>
              <p>
                <i className="fas fa-envelope"></i> conectemoscl@gmail.com
              </p>
              <p>
                <i className="fas fa-clock"></i> Lun-Vie: 9:00 - 20:00 | Sáb: 9:00 - 19:00 | Dom: 9:00 - 14:00
              </p>

              {/* Redes sociales */}
              <div className="footer-social">
                <a href="https://www.facebook.com/" aria-label="Facebook" className="social-link" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a
                  href="https://www.instagram.com/conectemos.chile?igsh=MXNseDRxZGYyZGxwcA=="
                  aria-label="Instagram"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://www.tiktok.com/@psicologo.ngel.da?_t=ZM-8zSsKIxiNRG&_r=1"
                  aria-label="TikTok"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-tiktok"></i>
                </a>
                <a
                  href="https://wa.me/56978009119?text=Hola,%20me%20gustaría%20agendar%20una%20consulta%20en%20Conectemos%20Chile"
                  aria-label="WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  <i className="fab fa-whatsapp"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Información legal y créditos */}
          <div className="footer-bottom">
            <p>&copy; 2026 Conectemos SpA · RUT 77.528.735-7 · <a href="/privacidad">Política de privacidad</a></p>
            <p className="developer-credit">
              Desarrollo web por{' '}
              <a href="https://www.wisestlabs.cl" target="_blank" rel="noopener noreferrer">
                Wisest Labs
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Botones flotantes */}
      <FloatingButtons />

    </>
  );
};

export default HomePage;